import React, {forwardRef, Fragment, useCallback} from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import {batch} from 'react-redux';
import {IVerticalDropdownItem} from 'admin/resources/files/models/IVerticalDropdownItem';
import {MenuItem as MUIMenuItem, makeStyles} from '@material-ui/core';
import {IBaseFileItem} from 'models/resources/files';
import {useFilesAll} from 'admin/resources/files/views/all/hooks';
import {IResourceContentTypeNameEnum} from 'enums/upload';
import {Tooltip} from 'components-lib';
import {filesAllMessages} from 'admin/resources/files/views/all/utils';

interface IDropdownItemProps {
  file: IBaseFileItem;
  handleDropdownClose?: () => void;
  clickHandler?: () => void;
  item: IVerticalDropdownItem;
}

export const DropdownItem = forwardRef(({item, file, handleDropdownClose}: IDropdownItemProps, ref: any) => {
  const classes = useStyles();
  const {
    fileDownloading,
    openFileInNewTab,
    downloadFile,
    copyClickHandler,
    copyCurrentYearExpirableLinkHandler,
    copyNextYearExpirableLinkHandler,
  } = useFilesAll();
  const hasUrl = !!file?.url;
  const isCopyable = item.isCopyLink && hasUrl;
  const isCopyCurrentYearExpireableLinkable = item.isCopyCurrentYearExpireableLink && hasUrl;
  const isCopyNextYearExpireableLinkable = item.isCopyNextYearExpireableLink && hasUrl;
  const isOpenInNewWindow = item.isOpenInNewWindow && hasUrl;
  const isDownloadable = item.isDownload && !!file?.contentItemId;
  const isScormPackage = file?.contentType === IResourceContentTypeNameEnum.ScormResource;
  const shouldShowTooltip = isScormPackage && !hasUrl;
  const tooltipText = shouldShowTooltip ? filesAllMessages.scormHasNotShortUrlMessage : '';

  const handleMenuItemClick = useCallback(() => {
    console.log('=== DROPDOWN ITEM CLICK DEBUG ===');
    console.log('Item clicked:', item);
    console.log('File object:', file);
    console.log('Item properties:', {
      isCopyLink: item.isCopyLink,
      isCopyCurrentYearExpireableLink: item.isCopyCurrentYearExpireableLink,
      isCopyNextYearExpireableLink: item.isCopyNextYearExpireableLink,
      isOpenInNewWindow: item.isOpenInNewWindow,
      isDownload: item.isDownload,
    });

    batch(() => {
      handleDropdownClose();

      if (item.isCopyLink) {
        console.log('Executing copyClickHandler');
        return copyClickHandler();
      }

      if (item.isCopyCurrentYearExpireableLink) {
        if (!file?.url) {
          console.error('No URL available for file:', file);
          return;
        }

        return copyCurrentYearExpirableLinkHandler(file.url);
      }

      if (item.isCopyNextYearExpireableLink) {
        if (!file?.url) {
          console.error('No URL available for file:', file);
          return;
        }
        console.log('Calling copyNextYearExpirableLinkHandler');
        return copyNextYearExpirableLinkHandler(file.url);
      }

      if (item.isOpenInNewWindow) {
        console.log('Executing openFileInNewTab');
        return openFileInNewTab(file?.url);
      }

      if (item.isDownload) {
        console.log('Executing downloadFile');
        return downloadFile(file?.contentItemId);
      }

      console.log('No action matched - this should not happen');
    });
    console.log('=== END DROPDOWN ITEM CLICK DEBUG ===');
  }, [
    item,
    file,
    handleDropdownClose,
    openFileInNewTab,
    copyClickHandler,
    downloadFile,
    copyCurrentYearExpirableLinkHandler,
    copyNextYearExpirableLinkHandler,
  ]);
  return (
    <Fragment>
      <Tooltip placement="left-start" title={tooltipText}>
        {item.isCopyLink && (
          <CopyToClipboard ref={ref} text={file?.url}>
            <MUIMenuItem
              classes={{root: classes.root}}
              disabled={!isCopyable || item.disabled}
              onClick={handleMenuItemClick}
            >
              {item.text}{' '}
            </MUIMenuItem>
          </CopyToClipboard>
        )}
      </Tooltip>
      <Tooltip placement="left-start" title={tooltipText}>
        {item.isCopyCurrentYearExpireableLink && (
          <MUIMenuItem
            classes={{root: classes.root}}
            ref={ref}
            disabled={!isCopyCurrentYearExpireableLinkable || item.disabled}
            onClick={handleMenuItemClick}
          >
            {item.text}
          </MUIMenuItem>
        )}
      </Tooltip>
      <Tooltip placement="left-start" title={tooltipText}>
        {item.isCopyNextYearExpireableLink && (
          <MUIMenuItem
            classes={{root: classes.root}}
            ref={ref}
            disabled={!isCopyNextYearExpireableLinkable || item.disabled}
            onClick={handleMenuItemClick}
          >
            {item.text}
          </MUIMenuItem>
        )}
      </Tooltip>
      <Tooltip placement="left-start" title={tooltipText}>
        {item.isOpenInNewWindow && (
          <MUIMenuItem
            classes={{root: classes.root}}
            ref={ref}
            disabled={!isOpenInNewWindow || item.disabled}
            onClick={handleMenuItemClick}
          >
            {item.text}
          </MUIMenuItem>
        )}
      </Tooltip>
      {item.isDownload && (
        <MUIMenuItem
          classes={{root: classes.root}}
          ref={ref}
          disabled={!isDownloadable || item.disabled || fileDownloading}
          onClick={handleMenuItemClick}
        >
          {item.text}
        </MUIMenuItem>
      )}
    </Fragment>
  );
});

const useStyles = makeStyles(() => ({
  root: {
    fontWeight: 500,
  },
}));

DropdownItem.displayName = 'DropdownItem';
